<script lang="ts" setup>
import { SchemeResponse } from '@/services/models/SchemeResponse.ts'
import { copyToClipboard, writeJsonToClipboard } from '@/helper/tools.ts'
import formStore from '@/store/FormStore.ts'
import EnlargeBtn from '@/components/EnlargeBtn.vue'
import { StepsController as stepsController } from '@/controllers/StepsController.ts'
import { ref } from 'vue'
import LightBox from '@/components/LightBox.vue'
import ModalParams from '@/services/modals/ModalParams.ts'
import { ModalTypes } from '@/helper/ModalTypes.ts'
import { modalService } from '@/services/modals/ModalService.ts'
import userStore from '@/store/UserStore.ts'
import DeviationBadge from '@/components/badges/DeviationBadge.vue'

const props = defineProps<{
  scheme: SchemeResponse
}>()

const lightBoxImage = ref<HTMLImageElement | null>(null)

function selectSystemScheme(documentNumber: string): void {
  formStore.data.systemSchemeDocumentNumber = documentNumber
  stepsController.currentStepValid()
}

/**
 * Since schemes are implemented as radio buttons and scheme should be unselectable implements the reset option
 */
function componentClicked(e: Event): void {
  const $label = e.currentTarget as HTMLLabelElement
  if (($label.control as HTMLInputElement).checked) {
    e.preventDefault()
    formStore.data.systemSchemeId = ''
  }
}

function zoomClicked(e: Event): void {
  lightBoxImage.value = (e.currentTarget as HTMLElement)?.parentNode?.querySelector('img') || null
}

async function openDebugInfoModal(): Promise<void> {
  const modalParams = new ModalParams(ModalTypes.SCHEME_DEBUG_INFO, { scheme: props.scheme })
  await modalService.showModal(modalParams)
}
</script>

<template>
  <article
    :data-id="scheme.fileName"
    class="scheme select-card"
    @click.exact.alt.ctrl.shift.prevent="writeJsonToClipboard(scheme)"
    @click.exact.alt.prevent="copyToClipboard(scheme.fileName)"
  >
    <span
      v-if="userStore.isPrivileged"
      style="z-index: 2; position: relative; cursor:pointer; padding: 0.5rem;"
      @click.stop="openDebugInfoModal"
    >
      {{ scheme.fileName }}
    </span>

    <input
      :id="scheme.fileName"
      v-model="formStore.data.systemSchemeId"
      :value="scheme.id"
      name="scheme-selection"
      type="radio"
      @change="selectSystemScheme(scheme.documentNumber)"
    >
    <label :for="scheme.fileName" class="select-card__label" @click="componentClicked" />

    <div class="select-card__image-box scheme__image-box">
      <div class="scheme__image">
        <img :alt="scheme.fileName" :src="scheme.previewUrl" loading="lazy">
      </div>

      <EnlargeBtn :class="{ 'is--hidden': lightBoxImage }" class="scheme__zoom" @click="zoomClicked">
        <template #content>
          <i class="ic-search-plus" />
          <span>{{ $t('step_system_schemes.btn_zoom') }}</span>
        </template>
      </EnlargeBtn>
    </div>

    <div class="scheme__content">
      <div class="scheme__score-bar">
        {{ scheme.score.totalScorePercent }} %
      </div>

      <div v-if="scheme.score.missing.length" class="scheme__deviations">
        <p>{{ $t('step_system_schemes.title_missing') }}</p>

        <div class="deviation-badges">
          <DeviationBadge
            v-for="deviation in scheme.score.missing"
            :key="deviation"
            :deviation="deviation"
            type="missing"
          />
        </div>
      </div>

      <div v-if="scheme.score.unwanted.length" class="scheme__deviations">
        <p>{{ $t('step_system_schemes.title_unwanted') }}</p>

        <div class="deviation-badges">
          <DeviationBadge
            v-for="deviation in scheme.score.unwanted"
            :key="deviation"
            :deviation="deviation"
            type="unwanted"
          />
        </div>
      </div>
    </div>
  </article>

  <LightBox v-if="lightBoxImage" :source-img="lightBoxImage" @close="lightBoxImage = null" />
</template>

<style lang="scss" scoped>
.select-card {
  margin-bottom: 0;

  &__label {
    @include themify($themes) {
      border-radius: rem-calc(themed('cardBorderRadius'));
    }
  }
}

.scheme {
  display: flex;
  flex-direction: column;

  &__image {
    img {
      width: 100%;
      height: auto;
    }
  }

  &__content {
    padding: 0.75rem;
  }

  &__score-bar {
    text-align: center;
    padding: 0.75rem;
    font-weight: bold;

    .theme-va & {
      color: $vaPrimaryColor;
      background: $vaPrimaryLightColor;
      border-radius: 0.25rem;
    }

    .theme-sd & {
      color: $sdSecondaryColor;
      background: $sdSecondaryLightColor;
      border-radius: 0;
    }
  }

  &__deviations {
    margin-top: 0.75rem;

    p {
      color: $textColor;
    }

    .deviation-badges {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }
  }

  .select-card__image-box {
    display: grid;
    place-content: center;

    > * {
      grid-area: 1 / 1;
    }

    @include themify($themes) {
      border-radius: rem-calc(themed('cardBorderRadius'));
    }
  }

}

button.scheme__zoom {
  z-index: 1;
  opacity: 1;
  align-self: end;
  margin: 0 0 0.75rem 0.75rem;
  max-width: rem-calc(42);

  &.is--hidden {
    opacity: 0;
  }
}
</style>
